import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth/AuthProvider";
import { ApiError, apiFetch } from "@/lib/api/client";
import type { PromoCodeValidation } from "@/lib/api/hooks";
import { getStripe } from "@/lib/stripe";
import { track } from "@/lib/analytics";
import { cart, type CartItem } from "./stores/cart";

export interface CoursePurchaseRequest {
  sanityProductId: string;
  expectedPriceCents: number;
  promoCode: string | null;
  paymentMethodId: string;
  idempotencyKey: string;
}

export interface CoursePurchaseResult {
  purchaseId: string;
  productId: string;
  // A declined charge is a 402 `payment_failed`, never a status.
  status: "succeeded" | "requires_action" | "processing" | "cancelled";
  subtotalCents: number;
  discountCents: number;
  totalCents: number;
  paymentIntentId?: string;
  clientSecret?: string | null;
  pharmacyOrderId?: string | null;
  intakeFormId?: string | null;
  intakeStage?: "initial" | "renewal" | null;
  error?: { code: string; message: string } | null;
}

interface Attempt {
  request: CoursePurchaseRequest;
  item: CartItem;
}

/** A course line the code was validated for: its member price and the
 * discount the course checkout quoted for it. */
export interface CoursePromoLine {
  id: string;
  amount: number;
  discount: number;
}

const storageKey = (userId: string) =>
  `system.portal.course-purchase.${userId}.v1`;

export function readCourseAttempt(userId?: string): Attempt | null {
  if (!userId) return null;
  try {
    const value = JSON.parse(
      localStorage.getItem(storageKey(userId)) ?? "null",
    ) as Attempt | null;
    return value?.request?.idempotencyKey && value.item?.id ? value : null;
  } catch {
    return null;
  }
}

/** The post-purchase intake shape for a paid course (postPurchaseIntake.ts). */
export function courseIntakeLines(purchases: CoursePurchaseResult[]) {
  return purchases
    .filter((purchase) => purchase.pharmacyOrderId)
    .map((purchase) => ({
      pharmacy_order_id: purchase.pharmacyOrderId!,
      intake_required: Boolean(purchase.intakeFormId),
    }));
}

/** Paid, with the order still being parked: the payment is done. */
const orderPending = (result: CoursePurchaseResult) =>
  result.status === "processing" && result.error?.code === "order_pending";
/** Stripe's state was unknown or the order is parking: confirm again. */
const settling = (result: CoursePurchaseResult) =>
  orderPending(result) ||
  (result.status === "processing" &&
    result.error?.code === "payment_unconfirmed");

const post = (path: string, body: object) =>
  apiFetch<CoursePurchaseResult>(path, {
    method: "POST",
    body: JSON.stringify(body),
  });

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** One-time courses are single PaymentIntents on a saved card, one course per
 * request. An uncertain payment keeps its identity across drawer closes and
 * reloads; only a terminal result or a server-confirmed cancel releases it. */
export function useCourseCartPurchase() {
  const { session } = useAuth();
  const userId = session?.user.id;
  const currentUser = useRef(userId);
  currentUser.current = userId;
  const [saved, setSaved] = useState<{
    userId?: string;
    attempt: Attempt | null;
  }>(() => ({ userId, attempt: readCourseAttempt(userId) }));
  const [error, setError] = useState<string | null>(null);
  // The validate-promo-code reason when the course refused the code.
  const [promoRefusal, setPromoRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Paid courses the drawer has not routed to their intake yet. They survive
  // a later failure, so the eventual destination still includes them.
  const unrouted = useRef<CoursePurchaseResult[]>([]);
  const qc = useQueryClient();
  const attempt = saved.userId === userId ? saved.attempt : null;

  useEffect(() => {
    currentUser.current = userId;
    setSaved({ userId, attempt: readCourseAttempt(userId) });
    setError(null);
    setPromoRefusal(null);
    setBusy(false);
    unrouted.current = [];
    const sync = (event: StorageEvent) => {
      if (userId && event.key === storageKey(userId)) {
        setSaved({ userId, attempt: readCourseAttempt(userId) });
      }
    };
    window.addEventListener("storage", sync);
    return () => {
      currentUser.current = undefined;
      window.removeEventListener("storage", sync);
    };
  }, [userId]);

  function remember(next: Attempt | null) {
    if (!userId || currentUser.current !== userId)
      throw new Error("Please sign in again before purchasing.");
    if (next) {
      // A charge must not start unless its retry identity was durably saved.
      localStorage.setItem(storageKey(userId), JSON.stringify(next));
    } else {
      try {
        localStorage.removeItem(storageKey(userId));
      } catch {
        /* A retained terminal attempt is safe to reconcile. */
      }
    }
    setSaved({ userId, attempt: next });
  }

  /** Where the payment stands now. The intent exists, so a failure here keeps
   * the purchase's identity, except a declined charge (402), which is final. */
  function confirm(original: Attempt) {
    return post("/me/course-purchases/confirm", {
      idempotencyKey: original.request.idempotencyKey,
    }).catch((cause: unknown) => {
      if (cause instanceof ApiError && cause.status === 402) throw cause;
      throw new Error(
        cause instanceof Error
          ? cause.message
          : "We could not confirm the payment. Check purchase status shortly.",
      );
    });
  }

  /** Apply a server result. True once the course is paid. */
  async function settle(result: CoursePurchaseResult, original: Attempt) {
    if (currentUser.current !== userId) return false;
    if (result.status === "succeeded" || orderPending(result)) {
      cart.remove(original.item.id);
      remember(null);
      unrouted.current.push(result);
      await Promise.allSettled([
        qc.invalidateQueries({ queryKey: ["marketplace"], refetchType: "all" }),
        qc.invalidateQueries({ queryKey: ["me", "required-intakes"] }),
        qc.invalidateQueries({ queryKey: ["me", "clinical-state"] }),
        qc.invalidateQueries({ queryKey: ["me", "orders"] }),
        qc.invalidateQueries({ queryKey: ["home"] }),
      ]);
      return currentUser.current === userId;
    }
    if (result.status === "cancelled") {
      remember(null);
      setError(
        `${original.item.title}: ${result.error?.message ?? "Payment cancelled."}`,
      );
      return false;
    }
    remember(original);
    setError(
      result.status === "processing"
        ? (result.error?.message ??
            "Your course payment is processing. Check purchase status shortly.")
        : "Your payment still needs verification. Check purchase status to continue, or cancel the unpaid purchase.",
    );
    return false;
  }

  async function purchaseOne(
    original: Attempt,
    publishableKey: string | null,
  ): Promise<CoursePurchaseResult | null> {
    let result = await post("/me/course-purchases", original.request);
    if (currentUser.current !== userId) return null;
    if (result.status === "requires_action") {
      if (!publishableKey || !result.clientSecret)
        throw new Error(
          "Card verification is unavailable. Check purchase status shortly.",
        );
      const stripe = await getStripe(publishableKey);
      if (currentUser.current !== userId) return null;
      if (!stripe)
        throw new Error(
          "Card verification could not load. Check purchase status to retry.",
        );
      const verification = await stripe.handleNextAction({
        clientSecret: result.clientSecret,
      });
      if (currentUser.current !== userId) return null;
      // Stripe may have changed the intent even when its UI reports an error.
      // After a cancelled or failed verification the server closes the intent
      // with a 402: that is the verification failing, not a declined card.
      const reconciled = await confirm(original).catch((cause: unknown) => {
        if (
          verification.error &&
          cause instanceof ApiError &&
          cause.status === 402
        )
          return null;
        throw cause;
      });
      if (currentUser.current !== userId) return null;
      if (
        !reconciled ||
        (verification.error &&
          reconciled.status !== "succeeded" &&
          !orderPending(reconciled))
      ) {
        // A closed intent is spent; confirming again starts a new attempt.
        if (reconciled) await settle(reconciled, original);
        else remember(null);
        if (currentUser.current === userId)
          setError(
            verification.error?.message ?? "Your card could not be verified.",
          );
        return null;
      }
      result = reconciled;
    }
    // Give an unconfirmed payment or a parking order a few seconds to settle.
    for (let retry = 0; retry < 3 && settling(result); retry += 1) {
      await sleep(2000);
      if (currentUser.current !== userId) return null;
      result = await confirm(original);
      if (currentUser.current !== userId) return null;
    }
    return (await settle(result, original)) ? result : null;
  }

  /** Buy each course line in turn. Resolves the paid purchases once every
   * line is paid, or null when one still needs attention (paid lines have
   * already left the cart). A saved attempt is resumed and never buys new
   * lines. */
  async function purchase(
    items: CartItem[],
    paymentMethodId: string,
    // The code and the course lines that redeem it.
    promo: { code: string; productIds: string[] } | null,
    publishableKey: string | null,
  ): Promise<CoursePurchaseResult[] | null> {
    setBusy(true);
    setError(null);
    setPromoRefusal(null);
    const persisted = readCourseAttempt(userId);
    const queue: Attempt[] = persisted
      ? [persisted]
      : items.map((item) => ({
          item,
          request: {
            sanityProductId: item.id,
            expectedPriceCents: item.price_cents,
            promoCode: promo?.productIds.includes(item.id) ? promo.code : null,
            paymentMethodId,
            idempotencyKey: crypto.randomUUID(),
          },
        }));
    const paid: CoursePurchaseResult[] = [];
    let current: Attempt | undefined;
    try {
      for (current of queue) {
        if (current !== persisted) {
          remember(current);
          // The server records complete_purchase for courses; the client only
          // marks the start, so a course is never counted twice.
          track("checkout_started", {
            source: "marketplace",
            purchase_flow: "course",
            item_count: 1,
            product_ids: [current.item.id],
            price: current.item.price_cents / 100,
            duration: "one_time_course",
            promo_code: current.request.promoCode ?? undefined,
          });
        }
        const result = await purchaseOne(current, publishableKey);
        if (!result) return null;
        paid.push(result);
      }
      return paid;
    } catch (cause) {
      if (currentUser.current !== userId) return null;
      const code = cause instanceof ApiError ? cause.code : null;
      // A 4xx from the purchase request is a refusal before or instead of a
      // charge (course_in_progress, price_changed, product_unavailable, promo
      // codes, payment_failed), so the identity is spent. Anything else may
      // have charged: keep it.
      if (
        current &&
        cause instanceof ApiError &&
        cause.status >= 400 &&
        cause.status < 500
      )
        remember(null);
      if (
        current &&
        (code === "price_changed" ||
          code === "product_unavailable" ||
          code === "course_in_progress")
      ) {
        // The line cannot be bought as shown (or until the open course ships);
        // the Shop has the current terms.
        cart.remove(current.item.id);
        void qc.invalidateQueries({ queryKey: ["marketplace"] });
      }
      // Refused before any charge. A first-time code that couldn't be
      // verified (503) is a refusal too.
      const promoRefused =
        code === "promo_invalid" ||
        code === "promo_already_used" ||
        code === "promo_verification_unavailable";
      if (code === "promo_verification_unavailable") remember(null);
      if (promoRefused) {
        // The detail names the validate-promo-code reason beside the code.
        const reason = (cause as ApiError).payload?.reason;
        setPromoRefusal(
          typeof reason === "string"
            ? reason
            : code === "promo_already_used"
              ? "already_used"
              : code === "promo_verification_unavailable"
                ? "lookup_failed"
                : "not_found",
        );
      }
      setError(
        code === "price_changed"
          ? `The price of ${current?.item.title ?? "this course"} changed, so it was removed from your cart. Add it again from the Shop to see the current price.`
          : code === "product_unavailable" && cause instanceof Error
            ? `${current?.item.title ?? "This course"} was removed from your cart: ${cause.message}`
            : promoRefused
              ? "Your promo code could not be applied, so you were not charged. Remove or change the code to continue."
              : cause instanceof Error
                ? cause.message
                : "We could not confirm the payment. Check purchase status before trying a new purchase.",
      );
      return null;
    } finally {
      if (currentUser.current === userId) setBusy(false);
    }
  }

  /** Validate a code for the course lines that would redeem it: the first, or
   * every line when the code allows reuse. With `sanity_product_id` the server
   * applies the course checkout's own rules to the line's member price. */
  async function quotePromo(
    code: string,
    items: CartItem[],
    memberCents: (item: CartItem) => number,
  ) {
    const lines: CoursePromoLine[] = [];
    let applied: (PromoCodeValidation & { allow_reuse?: boolean }) | null =
      null;
    for (const item of items) {
      const validation = await apiFetch<
        PromoCodeValidation & { allow_reuse?: boolean }
      >("/me/validate-promo-code", {
        method: "POST",
        body: JSON.stringify({
          code,
          amount_cents: memberCents(item),
          sanity_product_id: item.id,
        }),
      });
      if (!validation.valid) return { validation, reuse: false, lines: [] };
      applied ??= validation;
      lines.push({
        id: item.id,
        amount: memberCents(item),
        discount: validation.discount_cents,
      });
      if (!validation.allow_reuse) break;
    }
    return {
      validation: applied,
      reuse: Boolean(applied?.allow_reuse),
      lines,
    };
  }

  /** Cancel an unpaid attempt. A course that turns out to be paid settles as
   * a purchase, so it resolves to that result. */
  async function cancel(): Promise<CoursePurchaseResult | null> {
    if (!attempt || currentUser.current !== userId) return null;
    const original = attempt;
    setBusy(true);
    setError(null);
    setPromoRefusal(null);
    try {
      const result = await post("/me/course-purchases/cancel", {
        idempotencyKey: original.request.idempotencyKey,
      }).catch((cause: unknown) => {
        if (cause instanceof ApiError && cause.code === "course_already_paid")
          return confirm(original);
        throw cause;
      });
      if (currentUser.current !== userId) return null;
      return (await settle(result, original)) ? result : null;
    } catch (cause) {
      if (currentUser.current !== userId) return null;
      setError(
        cause instanceof Error
          ? cause.message
          : "We could not cancel the unpaid purchase. Check its status before retrying.",
      );
      return null;
    } finally {
      if (currentUser.current === userId) setBusy(false);
    }
  }

  return {
    purchase,
    cancel,
    quotePromo,
    /** Paid courses since the last call, for post-purchase intake routing. */
    takePaid: () => unrouted.current.splice(0),
    attempt,
    busy,
    error,
    promoRefusal,
  };
}
